import Link from 'components/shared/Link/Link';
import Button from 'components/shared/Buttons/BaseButton';
import LinkWithIcon from 'components/shared/LinkWithIcon/LinkWithIcon';
import NavCoreButtonHorizontal from './NavCoreButtonHorizontal';
import NavCoreLinkHorizontal from './NavCoreLinkHorizontal';
import HorizontalNavSubMenu from './NavSubMenuHorizontal';
import CTA from 'components/shared/CTA/CTA';
import NavMenuButton from './NavMenuButton';
import MenuBack from '../../public/icons/menu-back.svg';
import { BackgroundColor } from 'types/constants';

import * as m from 'motion/react-m';

import { Cta, LinkedIconProps, ContentfulCollection } from 'libs/contentful/types';
import { NavContentLinkProps } from './types';
import { cn } from 'utils/styles';
import { AnimatePresence } from 'motion/react';
import { HEADER_NAV_ID_PREFIX, navTopBarProps } from 'components/MainNav/utils';
import ChimeLogo from 'components/shared/ChimeLogo/ChimeLogo';

export interface HorizontalNavBarProps {
  showShadows: boolean;
  topBarLink: LinkedIconProps;
  topBarCta: Cta;
  navContent: ContentfulCollection<NavContentLinkProps>;
  logoHref: string;
  menuIsOpen: boolean;
  subMenuIsOpen: boolean;
  openMenu: () => void;
  closeMenu: () => void;
  closeSubMenu: () => void;
  handleSubMenuClick: (m: NavContentLinkProps) => void;
  activeSubMenu: NavContentLinkProps | null;
}

const HorizontalNavBar = ({
  showShadows,
  navContent,
  topBarLink,
  topBarCta,
  logoHref,
  menuIsOpen,
  closeSubMenu,
  subMenuIsOpen,
  openMenu,
  closeMenu,
  handleSubMenuClick,
  activeSubMenu,
}: HorizontalNavBarProps) => {
  const mobileIdSuffix = 'small';
  const links = navContent.items;

  const crossFadeIn = { opacity: [0, 1], transition: { duration: 0.3 } };
  const crossFadeOut = { opacity: [1, 0], transition: { duration: 0.3 } };

  const homeLink = (
    <Link href={logoHref} id={`${HEADER_NAV_ID_PREFIX}_chime_logo`}>
      <ChimeLogo alt="Chime Home" />
    </Link>
  );

  const loginLink = (
    <LinkWithIcon
      id={`${HEADER_NAV_ID_PREFIX}_log_in`}
      href={topBarLink.url}
      internalPage={topBarLink.internalPage}
      chimeKitIconName={topBarLink.iconName}
      icon={topBarLink?.icon}
      className="font-semibold font-sans text-label-sans active:scale-none"
      sys={topBarLink?.sys}>
      {topBarLink.label}
    </LinkWithIcon>
  );

  const ctaButton = <CTA cta={topBarCta} className="min-w-0 px-2 sm:px-5" id={`${HEADER_NAV_ID_PREFIX}_enroll_link`} />;

  const menuButton = (idSuffix: string) => (
    <NavMenuButton
      subMenuIsOpen={subMenuIsOpen}
      menuIsOpen={menuIsOpen}
      openMenu={openMenu}
      closeMenu={closeMenu}
      closeSubMenu={closeSubMenu}
      idSuffix={idSuffix}
    />
  );

  const desktopNav = (
    <div className="md:flex hidden justify-between h-full">
      <div className="flex justify-between items-center w-full">
        <div className="flex items-center gap-4">
          {homeLink}
          <div className="flex items-center gap-4">
            {links.map((linkContent, i) => {
              if (linkContent.subMenu != null) {
                return <NavCoreButtonHorizontal linkContent={linkContent} handleClick={handleSubMenuClick} key={i} />;
              }
              if (linkContent.linkSize === 'Large' && linkContent.subMenu === null) {
                return <NavCoreLinkHorizontal linkContent={linkContent} key={i} />;
              }
            })}
          </div>
        </div>
        <div className="flex justify-end items-center gap-4">
          <div className="flex gap-4">
            {links.map((linkContent, i) => {
              if (linkContent.linkSize === 'Small') {
                return <NavCoreLinkHorizontal linkContent={linkContent} key={i} />;
              }
            })}
          </div>
          <div className="flex">{loginLink}</div>
          <div className="flex">{ctaButton}</div>
        </div>
      </div>
      {links.map((linkContent, i) => {
        if (linkContent.subMenu != null && activeSubMenu === linkContent) {
          return <HorizontalNavSubMenu linkContent={linkContent} key={i} />;
        }
      })}
    </div>
  );

  const mobileNav = (
    <div className="flex md:hidden justify-between h-full">
      {/* Mobile Left Section */}
      <div className="grid grid-rows-1 grid-cols-1 min-w-16">
        <AnimatePresence>
          {subMenuIsOpen ? (
            <m.div
              animate={crossFadeIn}
              exit={crossFadeOut}
              className="flex row-start-1 col-start-1 justify-start"
              key="back-button">
              <Button
                variant="icon"
                label={'Back'}
                onClick={closeSubMenu}
                className="pl-0 active:scale-none justify-start">
                <MenuBack />
              </Button>
            </m.div>
          ) : (
            <m.div
              animate={crossFadeIn}
              exit={crossFadeOut}
              className="flex row-start-1 col-start-1"
              key="mobile-home-link">
              {homeLink}
            </m.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Right Section */}
      <div className="flex gap-4 h-full">
        {/* Grid display makes content overlap on animation */}
        <div className="grid grid-rows-1 grid-cols-1">
          <AnimatePresence>
            {menuIsOpen ? (
              <m.div
                className="flex row-start-1 col-start-1 justify-end"
                key="login-link"
                animate={crossFadeIn}
                exit={crossFadeOut}>
                {loginLink}
              </m.div>
            ) : (
              <m.div
                className="flex row-start-1 col-start-1 "
                key="cta-button"
                animate={crossFadeIn}
                exit={crossFadeOut}>
                {ctaButton}
              </m.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex -mr-2.5">{menuButton(mobileIdSuffix)}</div>
      </div>
    </div>
  );

  return (
    <div
      {...navTopBarProps}
      className={cn(
        'w-full relative h-16 sm:h-18 z-50',
        BackgroundColor.White,
        showShadows && 'shadow-[10px_10px_20px] shadow-kale/5',
      )}>
      <div className="px-3 sm:px-14 h-full">
        <div className="py-4 h-full">
          {mobileNav}
          {desktopNav}
        </div>
      </div>
    </div>
  );
};

export default HorizontalNavBar;
